/**
 * Annual P&L (BE-14d-2)
 *
 * The P&L of a year month by month: each section's account lines and total in 12 month columns plus the year, then
 * gross profit, operating profit, net profit before tax, corporate tax and net profit. Accrual or cash basis.
 * The corporate tax line is on the year only (its months are "-"); with no estimate its total is "-" and its name
 * says why. Scrolls sideways on a phone.
 * Route: /reports/annual-pl
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Grid, TextField, MenuItem, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ReportPage, { formatCurrency } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { serverMessage } from '../../utils/apiConvert';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => THIS_YEAR - i);
const amount = (v) => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  return n === 0 ? '-' : n.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const AnnualPl = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [year, setYear] = useState(THIS_YEAR);
  const [basis, setBasis] = useState('ACCRUAL');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getAnnualPl({ year, basis }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load the annual P&L'));
    } finally {
      setLoading(false);
    }
  }, [year, basis]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // One table: a heading row per section, its account lines, its total; then the summary lines
  const rows = [];
  (report?.sections || []).forEach((s) => {
    rows.push({ kind: 'section', id: `s-${s.key}`, name: s.title });
    (s.lines || []).forEach((l) => rows.push({ ...l, kind: 'line', id: `${s.key}-${l.account}` }));
    if (s.totals) rows.push({ ...s.totals, kind: 'total', id: `t-${s.key}` });
  });
  (report?.summary || []).forEach((l, i) => rows.push({ ...l, kind: 'summary', id: `sum-${i}` }));

  const months = report?.months || [];
  const columns = [
    {
      key: 'name', label: 'Account',
      render: (r) => (r.kind === 'line' && r.account ? `${r.account} · ${r.name}` : r.name),
    },
    ...months.map((m, i) => ({
      key: `m${i}`, label: MONTH_NAMES[Number(m.slice(5, 7)) - 1] || m, align: 'right',
      render: (r) => (r.kind === 'section' ? '' : amount(r.months ? r.months[i] : null)),
    })),
    { key: 'total', label: String(report?.year || year), align: 'right', render: (r) => (r.kind === 'section' ? '' : amount(r.total)) },
  ];

  const rowSx = (r) => {
    if (r.kind === 'section') return { bgcolor: 'action.hover', '& td': { fontWeight: 700, fontSize: '0.8125rem' } };
    if (r.kind === 'total') return { '& td': { fontWeight: 700, borderTop: '1px solid', borderColor: 'divider' } };
    if (r.kind === 'summary') {
      const net = /^net profit$/i.test(r.name || '');
      return { bgcolor: net ? 'rgba(76,175,80,0.12)' : 'rgba(76,175,80,0.05)', '& td': { fontWeight: 700 } };
    }
    return {};
  };

  const revenue = (report?.sections || []).find((s) => s.key === 'revenue')?.totals?.total;
  const net = (report?.summary || []).find((l) => /^net profit$/i.test(l.name || ''))?.total;
  const cards = report ? [
    { label: `Revenue ${report.year}`, value: formatCurrency(revenue) },
    { label: `Net profit ${report.year}`, value: formatCurrency(net), tone: Number(net) < 0 ? 'bad' : 'good' },
    { label: 'Basis', value: report.basis === 'CASH' ? 'Cash' : 'Accrual' },
  ] : [];

  return (
    <ReportPage
      title="Annual P&L"
      subtitle={`Profit and loss for ${year} month by month (${basis === 'CASH' ? 'cash' : 'accrual'} basis), amounts in AED`}
      exportType="annual-pl" exportParams={{ year, basis }} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={columns} rows={rows} rowKey={(r) => r.id} rowSx={rowSx}
      tableOnPhone numbered={false} pageSize={500}
      emptyMessage="Nothing posted in this year."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={2}>
            <TextField select fullWidth size="small" label="Year" value={year} onChange={(e) => setYear(e.target.value)}>
              {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <ToggleButtonGroup exclusive size="small" value={basis} onChange={(e, v) => v && setBasis(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.8125rem', px: 2 } }}>
              <ToggleButton value="ACCRUAL">Accrual</ToggleButton>
              <ToggleButton value="CASH">Cash</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default AnnualPl;
